import type { DataTableColumn, DataTableLayout } from '@/UI/DataTable/types';

/** Floor for a dragged column — matches API `min_value` on table preference widths. */
export const DATA_TABLE_MIN_COLUMN_WIDTH = 72;

/** Ceiling for a dragged column — matches API `max_value` on table preference widths. */
export const DATA_TABLE_MAX_COLUMN_WIDTH = 2000;

export function clampDataTableColumnWidth(
  width: number,
  minWidth?: number,
): number {
  const floor = Math.max(
    minWidth ?? DATA_TABLE_MIN_COLUMN_WIDTH,
    DATA_TABLE_MIN_COLUMN_WIDTH,
  );
  if (!Number.isFinite(width)) return floor;
  return Math.min(
    DATA_TABLE_MAX_COLUMN_WIDTH,
    Math.max(floor, Math.round(width)),
  );
}

export function applyDataTableColumnWidth(
  layout: DataTableLayout,
  columnId: string,
  width: number,
): DataTableLayout {
  return {
    ...layout,
    widths: { ...layout.widths, [columnId]: width },
  };
}

/** Numeric width only: a saved resize, or an explicit column `width` number. */
export function resolveDataTableColumnWidth<T>(
  column: Pick<DataTableColumn<T>, 'id' | 'width'>,
  layoutWidths: Record<string, number>,
): number | undefined {
  const saved = layoutWidths[column.id];
  if (typeof saved === 'number') return saved;
  return column.width;
}

/**
 * Sum sized columns for `scroll.x`. Unsized columns need `'max-content'`
 * so `table-layout: fixed` does not give them 0 leftover space.
 */
export function resolveDataTableScrollX<T>(
  columns: Pick<DataTableColumn<T>, 'id' | 'width'>[],
  layoutWidths: Record<string, number>,
): number | 'max-content' {
  let total = 0;
  for (const column of columns) {
    const width = resolveDataTableColumnWidth(column, layoutWidths);
    if (width === undefined) return 'max-content';
    total += width;
  }
  return total;
}
